import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTRPC } from "@/trpc";
import type { StartVerificationDialogProps } from "@/features/credentials/components/primary-source-runs/types";
import { describeRunAllowanceLimit } from "./run-allowance-message";
import { NpiSelection } from "./npi-selection";
import { LicensePreparation } from "./license-preparation";
import { VerificationConfirmation } from "./verification-confirmation";
import { usePreparationEditor } from "./use-preparation-editor";
import type { VerificationPreparation } from "./types";

export function WorkspaceStartVerificationDialog(
  props: StartVerificationDialogProps,
) {
  const trpc = useTRPC();
  const query = useQuery({
    ...trpc.psv.getVerificationPreparation.queryOptions(props.scope),
    refetchOnWindowFocus: false,
  });
  if (query.data) {
    return (
      <LoadedPreparationDialog
        {...props}
        initialPreparation={query.data}
        onReload={async () => {
          const result = await query.refetch();
          if (result.error) {
            throw result.error;
          }
          if (!result.data) {
            throw new Error("Provider details are unavailable.");
          }
          return result.data;
        }}
      />
    );
  }
  return (
    <PreparationDialog {...props} pending={false}>
      {query.isPending && (
        <p role="status" className="text-sm text-muted-foreground">
          Loading provider details…
        </p>
      )}
      {query.isError && (
        <div role="alert" className="space-y-2 text-sm text-destructive">
          <p>Couldn’t load the provider details: {query.error.message}</p>
          <Button
            variant="outline"
            disabled={query.isFetching}
            onClick={() => void query.refetch()}
          >
            Retry provider details
          </Button>
        </div>
      )}
    </PreparationDialog>
  );
}

function LoadedPreparationDialog({
  initialPreparation,
  onReload,
  ...props
}: StartVerificationDialogProps & {
  initialPreparation: VerificationPreparation;
  onReload: () => Promise<VerificationPreparation>;
}) {
  const trpc = useTRPC();
  // Advisory only: while loading or errored the user is not blocked; the server still enforces the limit.
  const { data: allowance } = useQuery(
    trpc.psv.getRunAllowance.queryOptions({
      organizationBusinessUnitId: props.scope.organizationBusinessUnitId,
    }),
  );
  const limitReached = allowance?.limitReached === true;
  const editor = usePreparationEditor({
    initialPreparation,
    onReload,
    scope: props.scope,
    onStarted: props.onStarted,
  });
  return (
    <PreparationDialog {...props} pending={editor.pending}>
      {!editor.preparation.providerName && (
        <p role="alert" className="text-sm text-amber-900">
          Add the provider’s full name in this workspace before starting
          verification.
        </p>
      )}
      {allowance && limitReached && (
        <p role="alert" className="text-sm text-amber-900">
          {describeRunAllowanceLimit(allowance)}
        </p>
      )}
      <NpiSelection
        providerName={editor.preparation.providerName || "This provider"}
        savedNpi={editor.preparation.npi}
        hasSavedLicenses={editor.preparation.licenses.length > 0}
        selected={editor.selectedNpi}
        onChange={editor.selectNpi}
        onConfirm={() => void editor.fillLicenses()}
        pending={editor.pending}
        lookingUp={editor.action === "lookup"}
        warning={editor.lookupWarning}
      />
      <LicensePreparation
        preparation={editor.preparation}
        licenses={editor.licenses}
        skippedStateCodes={editor.skippedStateCodes}
        noneSelected={editor.noneSelected}
        onStateIncludedChange={editor.setStateIncluded}
        suggestions={editor.suggestions}
        pending={editor.pending}
        duplicates={editor.duplicates}
        tooManyChanges={editor.tooManyChanges}
        onChange={editor.editLicenses}
      />
      {editor.error && (
        <div role="alert" className="space-y-2 text-sm text-destructive">
          <p>{editor.error}</p>
          <Button
            variant="outline"
            size="sm"
            disabled={editor.pending}
            onClick={() => void editor.reload()}
          >
            Reload saved details
          </Button>
        </div>
      )}
      <VerificationConfirmation
        ready={editor.ready && !limitReached}
        skippedCount={editor.skippedCount}
        confirmed={editor.confirmed}
        onConfirmedChange={editor.setConfirmed}
        pending={editor.pending}
        starting={editor.action === "start"}
        onStart={() => void editor.startVerification()}
        onCancel={props.onClose}
      />
    </PreparationDialog>
  );
}

function PreparationDialog({
  pending,
  children,
  onClose,
  onRestoreFocus,
}: Pick<StartVerificationDialogProps, "onClose" | "onRestoreFocus"> & {
  pending: boolean;
  children: ReactNode;
}) {
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !pending) {
          onClose();
        }
      }}
    >
      <DialogContent
        className="max-h-[85dvh] w-full max-w-xl gap-4 overflow-y-auto"
        showCloseButton={!pending}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          onRestoreFocus();
        }}
        onEscapeKeyDown={(event) => {
          if (pending) {
            event.preventDefault();
          }
        }}
        onInteractOutside={(event) => {
          if (pending) {
            event.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Prepare verification</DialogTitle>
          <DialogDescription>
            Review the provider’s NPI and state licenses below, then confirm the
            details to start verification.
          </DialogDescription>
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}
